import { useCallback, useEffect, useMemo, useState } from "react";
import { Grid, Button } from "@mui/material";
import { useIntl } from "react-intl";

import AddIcon from "@mui/icons-material/Add";

import { TemplateItemProps } from "../types";
import { Zone } from "./types";
import { uuidv4 } from "../../../../utils/utils";
import ZoneItem from "./ZoneItem";

interface ZoneListProps extends TemplateItemProps<Zone[]> {}

const ZoneList = ({ value, saved, onChange, disabled }: ZoneListProps) => {
  const [indexes, setIndexes] = useState<number[]>([]);
  const { formatMessage } = useIntl();

  const onZoneChange = useCallback(
    (index: number, v: Zone) => {
      if (!value) {
        return;
      }
      value[index] = v;
      onChange(value);
    },
    [value, onChange]
  );

  const addNewZone = useCallback(() => {
    value.push({ uuid: uuidv4(), address: "" });
    onChange(value);
    setIndexes(value.map((v, i) => i));
  }, [value, onChange]);

  const removeZone = useCallback(
    (index) => {
      value[index].deleted = true;
      onChange(value);
    },
    [value, onChange]
  );

  const restoreZone = useCallback(
    (index) => {
      if (!value) {
        return;
      }
      delete value[index].deleted;
      onChange(value);
    },
    [value, onChange]
  );

  const getDefaultZone = useCallback(
    (index: number): string => {
      if (index === 0) {
        return "1";
      }
      let i = index - 1;
      while (i > -1) {
        if (value[i].zone) {
          break;
        }
        i--;
      }
      if (i === -1) {
        return `${index + 1}`;
      }
      return (parseInt(value[i].zone || "1", 16) + (index - i)).toString(16).toUpperCase();
    },
    [value]
  );

  const getDefaultType = useCallback(
    (index: number): string => {
      if (index === 0) {
        return "BLE";
      }
      let i = index - 1;
      while (i > -1) {
        if (value[i].type) {
          break;
        }
        i--;
      }
      if (i === -1) {
        return "BLE";
      }
      return value[i].type || "BLE";
    },
    [value]
  );

  const getDefaultReport = useCallback(
    (index: number): string => {
      if (index === 0) {
        return "";
      }
      let i = index - 1;
      while (i > -1) {
        if (value[i].report) {
          break;
        }
        i--;
      }
      if (i === -1) {
        return "";
      }
      return value[i].report || "";
    },
    [value]
  );

  const getDefaultAlarm = useCallback(
    (index: number): string => {
      if (index === 0) {
        return "";
      }
      let i = index - 1;
      while (i > -1) {
        if (value[i].alarm) {
          break;
        }
        i--;
      }
      if (i === -1) {
        return "";
      }
      return value[i].alarm || "";
    },
    [value]
  );

  useEffect(() => {
    setIndexes(value.map((_, i) => i));
  }, [value]);

  const zones = useMemo(
    () => (
      <Grid container spacing={1}>
        {indexes?.length === value.length &&
          indexes.map((i) => (
            <Grid item xs={12} key={`report-rule-${value[i].uuid}`}>
              <ZoneItem
                index={i}
                value={value[i]}
                saved={saved?.[i] || null}
                onChange={onZoneChange}
                onDelete={removeZone}
                onRestore={restoreZone}
                disabled={disabled}
                defaultZone={getDefaultZone(i)}
                defaultType={getDefaultType(i)}
                defaultReport={getDefaultReport(i)}
                defaultAlarm={getDefaultAlarm(i)}
              />
            </Grid>
          ))}
      </Grid>
    ),
    [indexes, value, saved, onZoneChange, removeZone, restoreZone, disabled, getDefaultZone, getDefaultType, getDefaultReport, getDefaultAlarm]
  );

  return (
    <Grid container spacing={1} style={{ padding: 8 }}>
      {zones}
      <Grid item xs={12}>
        <Button startIcon={<AddIcon />} onClick={() => addNewZone()} disabled={disabled}>
          {formatMessage({ defaultMessage: "New zone" })}
        </Button>
      </Grid>
    </Grid>
  );
};

export default ZoneList;
